import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";

import {
  InviteResolution,
  InviteState,
  TOO_MANY_ATTEMPTS,
  isThrottled,
  login,
  passwordReset,
  passwordResetConfirm,
  resolveInvite,
  setInviteName,
} from "@/api/auth";

/** validating -> one of the six invite states -> (from valid) success. */
type ClaimState = "validating" | InviteState | "error" | "success";

const HELP_EMAIL = "invites@getcodeclear.com";

// -- password read (no library; matches the spec's evaluatePassword) ---------
const rulesFor = (pw: string) => ({
  len: pw.length >= 12,
  case: /[A-Z]/.test(pw) && /[a-z]/.test(pw),
  symbol: /[0-9]/.test(pw) || /[^A-Za-z0-9]/.test(pw),
});

const strengthFor = (pw: string) => {
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/[0-9]/.test(pw) || /[^A-Za-z0-9]/.test(pw)) s++;
  return { score: s, label: ["Too short", "Weak", "Fair", "Good", "Strong"][s] };
};

// ============================================================================
// Small presentational atoms (warm --cl-* tokens via Tailwind arbitrary values)
// ============================================================================

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    className="text-[11px] uppercase tracking-[0.22em] text-[var(--cl-teal-ink)]"
    style={{ fontFamily: "var(--cl-mono)" }}
  >
    {children}
  </span>
);

const Serif: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className = "",
  children,
}) => (
  <h1
    className={`text-[28px] leading-[1.12] text-[var(--cl-ink)] ${className}`}
    style={{ fontFamily: "var(--cl-serif)", textWrap: "balance" }}
  >
    {children}
  </h1>
);

const HelpLine: React.FC<{
  text: string;
  to?: string;
  href?: string;
  linkLabel?: string;
}> = ({ text, to, href, linkLabel }) => (
  <p
    className="mt-6 text-[12px] leading-relaxed text-[var(--cl-mute)]"
    style={{ fontFamily: "var(--cl-sans)" }}
  >
    {text}
    {linkLabel && to && (
      <>
        {" "}
        <Link to={to} className="text-[var(--cl-teal-ink)] underline">
          {linkLabel}
        </Link>
      </>
    )}
    {linkLabel && href && (
      <>
        {" "}
        <a href={href} className="text-[var(--cl-teal-ink)] underline">
          {linkLabel}
        </a>
      </>
    )}
  </p>
);

// A big teal button rendered as a route Link, a mailto anchor, or a real button.
const btnClass =
  "inline-flex w-full items-center justify-center rounded-[8px] bg-[var(--cl-teal)] px-5 py-3 text-[14px] font-semibold text-[var(--cl-btn-ink)] transition-opacity hover:opacity-90 disabled:opacity-45";

// ============================================================================
// The six non-happy states + success — one card, one config map (no dead ends)
// ============================================================================

interface StatusScreen {
  icon: string;
  heading: string;
  body: string;
  action: { label: string; to?: string; href?: string; run?: () => void };
  help: { text: string; to?: string; href?: string; linkLabel?: string };
}

const CenteredCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="w-full max-w-[480px] rounded-[14px] border border-[var(--cl-line)] bg-[var(--cl-surface)] p-8 sm:p-10 text-center shadow-sm"
    style={{ fontFamily: "var(--cl-sans)" }}
  >
    {children}
  </div>
);

const StatusCard: React.FC<{ screen: StatusScreen }> = ({ screen }) => (
  <CenteredCard>
    <div
      aria-hidden="true"
      className="mx-auto mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--cl-teal-tint)] text-[20px] text-[var(--cl-teal-ink)]"
    >
      {screen.icon}
    </div>
    <Serif className="!text-[22px]">{screen.heading}</Serif>
    <p className="mt-3 text-[14px] leading-relaxed text-[var(--cl-ink-2)]">
      {screen.body}
    </p>
    <div className="mt-7">
      {screen.action.to && (
        <Link to={screen.action.to} className={btnClass}>
          {screen.action.label}
        </Link>
      )}
      {screen.action.href && (
        <a href={screen.action.href} className={btnClass}>
          {screen.action.label}
        </a>
      )}
      {screen.action.run && (
        <button type="button" className={btnClass} onClick={screen.action.run}>
          {screen.action.label}
        </button>
      )}
    </div>
    <HelpLine
      text={screen.help.text}
      to={screen.help.to}
      href={screen.help.href}
      linkLabel={screen.help.linkLabel}
    />
  </CenteredCard>
);

// ============================================================================
// The valid-state form
// ============================================================================

const WhoBox: React.FC<{ companyLine: string; invitedBy?: string | null }> = ({
  companyLine,
  invitedBy,
}) => (
  <dl className="mt-8 space-y-4">
    <div>
      <dt
        className="text-[8.5px] uppercase tracking-[0.18em] text-[var(--cl-faint)]"
        style={{ fontFamily: "var(--cl-mono)" }}
      >
        Joining
      </dt>
      <dd className="mt-1 text-[14px] text-[var(--cl-ink)]">{companyLine}</dd>
    </div>
    {/* Omit the row entirely when the inviter is unknown (never "Invited by —"). */}
    {invitedBy && (
      <div>
        <dt
          className="text-[8.5px] uppercase tracking-[0.18em] text-[var(--cl-faint)]"
          style={{ fontFamily: "var(--cl-mono)" }}
        >
          Invited by
        </dt>
        <dd className="mt-1 text-[14px] text-[var(--cl-ink)]">{invitedBy}</dd>
      </div>
    )}
  </dl>
);

const inputClass =
  "w-full rounded-[8px] border border-[var(--cl-line-2)] bg-[var(--cl-raise)] px-3.5 py-2.5 text-[14px] text-[var(--cl-ink)] outline-none placeholder:text-[var(--cl-faint)] focus:border-[var(--cl-teal)]";

const labelClass =
  "mb-1.5 block text-[8.5px] uppercase tracking-[0.18em] text-[var(--cl-mute)]";

const PasswordForm: React.FC<{
  token: string;
  email: string;
  prefill: InviteResolution;
  isReset: boolean;
  onOutcome: (s: ClaimState) => void;
}> = ({ token, email, prefill, isReset, onOutcome }) => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState(prefill.firstName ?? "");
  const [lastName, setLastName] = useState(prefill.lastName ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pwError, setPwError] = useState("");
  const [errorField, setErrorField] = useState<
    "" | "name" | "password" | "confirm"
  >("");

  const rules = useMemo(() => rulesFor(password), [password]);
  const strength = useMemo(() => strengthFor(password), [password]);
  const matches = password.length > 0 && password === confirm;

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    // Source of truth is the live DOM, not React state: a password manager can
    // autofill a field without firing onChange, leaving state stale while the
    // field looks full. Read + re-sync so the gate can never silently block.
    const fd = new FormData(e.currentTarget);
    const first = String(fd.get("firstName") ?? "").trim();
    const last = String(fd.get("lastName") ?? "").trim();
    const pw = String(fd.get("password") ?? "");
    const cpw = String(fd.get("confirm") ?? "");
    setFirstName(first);
    setLastName(last);
    setPassword(pw);
    setConfirm(cpw);
    setErrorField("");

    // Validate with a SPECIFIC message, so the user is never stuck without a
    // why (the old disabled-button gate explained nothing).
    const r = rulesFor(pw);
    if (!isReset && (!first || !last)) {
      setPwError("Add your first and last name.");
      setErrorField("name");
      return;
    }
    if (!r.len || !r.case || !r.symbol) {
      setPwError(
        "Your password needs 12 or more characters, upper and lower case, and a number or symbol.",
      );
      setErrorField("password");
      return;
    }
    if (pw !== cpw) {
      setPwError("The two passwords do not match.");
      setErrorField("confirm");
      return;
    }

    setSubmitting(true);
    setPwError("");
    try {
      // Invite only: persist the name first, while the token is still live
      // (the library confirm below consumes it). A reset just sets the password.
      if (!isReset) await setInviteName(token, first, last);
      await passwordResetConfirm(token, pw);
    } catch (err: unknown) {
      setSubmitting(false);
      if (isThrottled(err)) {
        setPwError(TOO_MANY_ATTEMPTS);
        return;
      }
      const data = (err as { response?: { data?: Record<string, unknown> } })
        ?.response?.data;
      const pwMsg = data?.password;
      if (pwMsg) {
        setPwError(Array.isArray(pwMsg) ? pwMsg.join(" ") : String(pwMsg));
        setErrorField("password");
        return;
      }
      // Token changed under us (consumed/expired between load and submit).
      if (data?.state && typeof data.state === "string") {
        onOutcome(data.state as ClaimState);
        return;
      }
      onOutcome("error");
      return;
    }
    // Password set. Try the "…and sign in" handoff; fall back to the success
    // card if auto-login fails (the account is still fully set up).
    try {
      await login(email, pw);
      navigate({ to: "/portal" });
    } catch {
      onOutcome("success");
    }
  };

  const barColor = (i: number) => {
    if (i >= strength.score) return "var(--cl-line-2)";
    if (strength.score <= 1) return "var(--cl-danger)";
    if (strength.score === 2) return "var(--cl-ochre)";
    return "var(--cl-teal)";
  };

  const Check: React.FC<{ ok: boolean; children: React.ReactNode }> = ({
    ok,
    children,
  }) => (
    <li
      className="flex items-center gap-2 text-[11px]"
      style={{
        fontFamily: "var(--cl-mono)",
        color: ok ? "var(--cl-teal-ink)" : "var(--cl-faint)",
      }}
    >
      <span aria-hidden="true">{ok ? "✓" : "○"}</span>
      {children}
    </li>
  );

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      {/* sr-only username: ties the saved password to this account AND is what
          makes the browser offer to suggest a strong password below. */}
      <input
        type="email"
        name="username"
        autoComplete="username"
        value={email}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
      />
      {!isReset && (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="cl-first" className={labelClass}>
            First name
          </label>
          <input
            id="cl-first"
            name="firstName"
            autoComplete="given-name"
            className={`${inputClass} ${
              errorField === "name" ? "border-[var(--cl-danger)]" : ""
            }`}
            value={firstName}
            maxLength={40}
            required
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="cl-last" className={labelClass}>
            Last name
          </label>
          <input
            id="cl-last"
            name="lastName"
            autoComplete="family-name"
            className={`${inputClass} ${
              errorField === "name" ? "border-[var(--cl-danger)]" : ""
            }`}
            value={lastName}
            maxLength={40}
            required
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
      </div>
      )}

      <div>
        <label htmlFor="cl-pw" className={labelClass}>
          New password
        </label>
        <div className="relative">
          <input
            id="cl-pw"
            name="password"
            type={show ? "text" : "password"}
            autoComplete="new-password"
            className={`${inputClass} pr-14 ${
              errorField === "password" ? "border-[var(--cl-danger)]" : ""
            }`}
            placeholder="At least 12 characters"
            value={password}
            required
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] uppercase tracking-[0.1em] text-[var(--cl-teal-ink)]"
            style={{ fontFamily: "var(--cl-mono)" }}
          >
            {show ? "hide" : "show"}
          </button>
        </div>
        {/* strength meter */}
        <div className="mt-2 flex items-center gap-2">
          <div className="flex flex-1 gap-1">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className="h-1 flex-1 rounded-full"
                style={{ backgroundColor: barColor(i) }}
              />
            ))}
          </div>
          <span
            className="text-[10px] uppercase tracking-[0.12em] text-[var(--cl-mute)]"
            style={{ fontFamily: "var(--cl-mono)" }}
          >
            {strength.label}
          </span>
        </div>
        <ul className="mt-3 space-y-1">
          <Check ok={rules.len}>12 or more characters</Check>
          <Check ok={rules.case}>Upper and lower case</Check>
          <Check ok={rules.symbol}>A number or symbol</Check>
        </ul>
        {pwError && (
          <p className="mt-2 text-[12px] text-[var(--cl-danger)]">{pwError}</p>
        )}
      </div>

      <div>
        <label htmlFor="cl-confirm" className={labelClass}>
          Confirm password
        </label>
        <input
          id="cl-confirm"
          name="confirm"
          type={show ? "text" : "password"}
          autoComplete="new-password"
          className={`${inputClass} ${
            errorField === "confirm" ? "border-[var(--cl-danger)]" : ""
          }`}
          placeholder="Type it again"
          value={confirm}
          required
          onChange={(e) => setConfirm(e.target.value)}
        />
        {confirm.length > 0 && !matches && (
          <p className="mt-1.5 text-[12px] text-[var(--cl-danger)]">
            Passwords do not match yet.
          </p>
        )}
      </div>

      <button type="submit" className={btnClass} disabled={submitting}>
        {submitting
          ? "Setting up…"
          : isReset
            ? "Reset password and sign in"
            : "Set password and sign in"}
      </button>
    </form>
  );
};

const ClaimForm: React.FC<{
  token: string;
  data: InviteResolution;
  isReset: boolean;
  onOutcome: (s: ClaimState) => void;
}> = ({ token, data, isReset, onOutcome }) => {
  const companyLine =
    data.buildingsCount != null
      ? `${data.companyName} · ${data.buildingsCount} building${
          data.buildingsCount === 1 ? "" : "s"
        }`
      : (data.companyName ?? "");
  return (
    <div className="grid w-full max-w-[880px] grid-cols-1 overflow-hidden rounded-[14px] border border-[var(--cl-line)] shadow-sm lg:grid-cols-2">
      <aside
        className="bg-[var(--cl-surface-2)] p-8 sm:p-10"
        style={{ fontFamily: "var(--cl-sans)" }}
      >
        <Eyebrow>CodeClear</Eyebrow>
        <Serif className="mt-4">
          {isReset ? "Reset your password" : "Set your password"}
        </Serif>
        <p className="mt-3 text-[14px] leading-relaxed text-[var(--cl-ink-2)]">
          {isReset
            ? `Choose a new password for ${data.email ?? "your account"}.`
            : "One step, then you are in. The last thing between you and your portfolio."}
        </p>
        {!isReset && (
          <WhoBox companyLine={companyLine} invitedBy={data.invitedBy} />
        )}
      </aside>
      <div
        className="bg-[var(--cl-surface)] p-8 sm:p-10"
        style={{ fontFamily: "var(--cl-sans)" }}
      >
        <PasswordForm
          token={token}
          email={data.email ?? ""}
          prefill={data}
          isReset={isReset}
          onOutcome={onOutcome}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Root
// ============================================================================

const ResetPassword: React.FC = () => {
  const { token, mode } = useSearch({
    from: "/external/password_reset_confirm",
  });
  const isReset = mode === "reset";
  const [state, setState] = useState<ClaimState>("validating");
  const [data, setData] = useState<InviteResolution | null>(null);
  const [resent, setResent] = useState(false);

  const resolve = useCallback(() => {
    setState("validating");
    resolveInvite(token ?? "")
      .then((res) => {
        setData(res);
        setState(res.state);
      })
      .catch(() => setState("error"));
  }, [token]);

  useEffect(() => {
    resolve();
  }, [resolve]);

  const sendNewLink = useCallback(() => {
    if (!data?.email) return;
    passwordReset(data.email).finally(() => setResent(true));
  }, [data]);

  const screens: Record<
    "expired" | "used" | "invalid" | "revoked" | "error",
    StatusScreen
  > = {
    expired: {
      icon: "⏱",
      heading: "This link has expired",
      body: "Links last 72 hours for security. Request a fresh one and we send it to the same address right away.",
      action: { label: "Send me a new link", run: sendNewLink },
      help: {
        text: "Or reply to your invite email, or reach your CodeClear contact.",
      },
    },
    used: {
      icon: "✓",
      heading: "This link was already used",
      body: "Your account is set up. Sign in with your password, or reset it if you have forgotten.",
      action: { label: "Go to sign in", to: "/login" },
      help: {
        text: "Forgot your password?",
        to: "/external/password_reset_request",
        linkLabel: "Reset it here.",
      },
    },
    invalid: {
      icon: "⚠",
      heading: "This link is not valid",
      body: "It may have been cut off by your email client. Open it again from the email, or ask for a new one.",
      action: { label: "Request a new link", to: "/external/password_reset_request" },
      help: {
        text: "Or reply to your invite email, or reach your CodeClear contact.",
      },
    },
    revoked: {
      icon: "⊘",
      heading: "This invite was withdrawn",
      body: `An admin at ${
        data?.companyName ?? "your company"
      } revoked this invitation. If that is a surprise, check with them, or contact us.`,
      action: { label: "Contact CodeClear", href: `mailto:${HELP_EMAIL}` },
      help: { text: "Or reach your CodeClear contact." },
    },
    error: {
      icon: "↻",
      heading: "Something went wrong on our side",
      body: "This one is on us, not you. Give it a moment and try again. If it keeps happening we sort it out with you.",
      action: { label: "Try again", run: resolve },
      help: { text: "Or reach your CodeClear contact." },
    },
  };

  let body: React.ReactNode;
  if (state === "validating") {
    body = (
      <CenteredCard>
        <div className="mx-auto mb-5 h-8 w-8 animate-spin rounded-full border-[3px] border-[var(--cl-line-2)] border-t-[var(--cl-teal)]" />
        <Serif className="!text-[22px]">Checking your link</Serif>
        <p className="mt-3 text-[14px] text-[var(--cl-ink-2)]">
          One moment while we verify your invitation. This only takes a second.
        </p>
      </CenteredCard>
    );
  } else if (state === "valid" && data) {
    body = (
      <ClaimForm
        token={token ?? ""}
        data={data}
        onOutcome={setState}
        isReset={isReset}
      />
    );
  } else if (state === "success") {
    body = (
      <CenteredCard>
        <div
          aria-hidden="true"
          className="mx-auto mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--cl-teal-tint)] text-[20px] text-[var(--cl-teal-ink)]"
        >
          ✓
        </div>
        <Serif className="!text-[22px]">You are all set</Serif>
        <p className="mt-3 text-[14px] text-[var(--cl-ink-2)]">
          Your password is set. Sign in to open your portfolio.
        </p>
        <div className="mt-7">
          <Link to="/login" className={btnClass}>
            Go to sign in
          </Link>
        </div>
      </CenteredCard>
    );
  } else {
    const screen = screens[state as keyof typeof screens] ?? screens.error;
    body = (
      <div className="w-full max-w-[480px]">
        <StatusCard screen={screen} />
        {state === "expired" && resent && (
          <p className="mt-3 text-center text-[12px] text-[var(--cl-teal-ink)]">
            Sent. Check your inbox for a fresh link.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="claim-scope flex min-h-screen w-full items-center justify-center bg-[var(--cl-paper)] px-4 py-10">
      {body}
    </div>
  );
};

export default ResetPassword;
